import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { message, Modal } from 'antd';
import NewButton from '../components/NewButton';
import useFormRef from '../hooks/useFormRef';
import { Mode } from '../store/types';
import { useStoreActions, useStoreState } from '../store/hooks';
import { IdAndNameType, MinMargin, RuleStates } from '../api/types';
import MinMarginsTable from '../components/MinMarginsTable';
import { defaultMinMargin } from '../store/minMargins';
import MinMarginForm from '../components/MinMarginForm';
import RuleDetailFormButtons from '../components/RuleDetailFormButtons';
import RuleFormTitle from '../components/RuleFromTitle';

const MinMarginsPage: React.FC = () => {
  const [isVisible, setVisible] = useState(false);
  const [isVisibleDC, setVisibleDC] = useState(false);

  const [mode, setMode] = useState<Mode>(Mode.VIEW);
  const { saveFormRef, formRef } = useFormRef<MinMargin>();
  const {
    setDetail,
    fetchMinMargin,
    createMinMargin,
    updateMinMargin,
    deleteMinMargin,
    fetchList,
    setSelectedDetailId,
  } = useStoreActions(actions => actions.minMargins);
  const { detail, selectedDetailId } = useStoreState(
    state => state.minMargins,
  );
  const { fetchCategories } = useStoreActions(actions => actions.general);
  const categoriesList = useStoreState(state => state.general.categoriesList);
  const { formatMessage } = useIntl();

  useEffect(() => {
    fetchList();
    fetchCategories();
  }, [fetchList, fetchCategories]);

  const handleEdit = async (id: number) => {
    await fetchMinMargin(id);
    setMode(Mode.EDIT);
    setVisible(true);
  };

  const handleRemove = async (id: number) => {
    setSelectedDetailId(id);
    setVisibleDC(true);
  };

  const handleView = async (id: number) => {
    await fetchMinMargin(id);
    setMode(Mode.VIEW);
    setVisible(true);
  };

  const getDefaultValue = (): IdAndNameType => {
    if (!categoriesList || categoriesList.count() === 0
    ) {
      return { id: '', name: '' };
    }

    const val = categoriesList.find(cL => cL.id === 'DEFAULT');
    return val ? { id: val.id, name: val.name } : { id: '', name: '' };
  };

  const handleNew = () => {
    if (categoriesList && categoriesList.count() > 0) {
      defaultMinMargin.category = getDefaultValue();
    }

    setDetail(defaultMinMargin);
    setMode(Mode.CREATE);
    setVisible(true);
  };

  const getCategoryValue = (category: IdAndNameType): IdAndNameType => {
    if (
      !category ||
      !category.id ||
      !categoriesList ||
      categoriesList.count() === 0
    ) {
      return { id: '', name: '' };
    }

    const val = categoriesList.find(cL => cL.id === category.id);
    return val ? { id: val.id, name: val.name } : { id: '', name: '' };
  };

  const handleSave = (newState: RuleStates) => {
    formRef!.validateFields(
      (err, { category, ...fieldsValue }) => {
        // @ts-ignore
        const inValues = {
          ...fieldsValue,
          // @ts-ignore
          category: getCategoryValue(category),
        } as MinMargin;

        if (!err) {
          switch (mode) {
            case Mode.CREATE:
              inValues.state = RuleStates.DRAFT;
              createMinMargin(inValues).then(res => {
                if (res) {
                  message.success(
                    formatMessage({ id: 'common.message.success_new' }),
                  );
                  setVisible(false);
                }
              });

              break;
            case Mode.EDIT:
              inValues.state = newState;
              updateMinMargin(inValues).then(res => {
                if (res) {
                  message.success(
                    formatMessage({ id: 'common.message.success_edit' }),
                  );
                  setVisible(false);
                }
              });

              break;
            default:
              break;
          }
        }
      },
    );
  };

  const handleDeleteSelected = () => {
    deleteMinMargin(selectedDetailId).then(res => {
      setVisibleDC(false);
      if (res) {
        message.success(formatMessage({ id: 'common.message.success_delete' }));
        setVisible(false);
      }
    });
  };

  const onCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setVisible(false);
    formRef!.resetFields();
  };


  return (
    <div className='max-discounts'>
      <NewButton onClick={handleNew} />
      <Modal
        title={<FormattedMessage id='common.confirmation' />}
        visible={isVisibleDC}
        width={700}
        onCancel={e => {
          e.preventDefault();
          setVisibleDC(false);
        }}
        okText={<FormattedMessage id='common.delete' />}
        onOk={handleDeleteSelected}
      >
        <FormattedMessage id='common.confirm_delete' />
      </Modal>
      <MinMarginsTable onEdit={handleEdit} onView={handleView} onRemove={handleRemove} />
      <Modal
        title={<RuleFormTitle mode={mode} ruleState={detail.state} messageId='min_margins.detail.title' />}
        visible={isVisible}
        width={700}
        footer={mode === Mode.VIEW ? null : <RuleDetailFormButtons
          ruleState={detail.state}
          mode={mode}
          onCancel={onCancel}
          onSave={newState => handleSave(newState)}
          onDelete={() => handleRemove(detail.id)}
        />}
        onCancel={onCancel}
      >
        <MinMarginForm ref={saveFormRef} data={detail} mode={mode} />
      </Modal>
    </div>
  );
};
export default MinMarginsPage;
